import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Spinner, Form, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaLaptopCode, FaMobileAlt, FaDesktop, FaShieldAlt, FaWindows, FaKey, FaCode, FaReact, FaNodeJs, FaDatabase, FaEnvelope, FaPhone, FaMapMarkerAlt, FaWhatsapp } from 'react-icons/fa';
import { SiMongodb, SiExpress, SiCsharp, SiDotnet, SiReact } from 'react-icons/si';
import ProductCard from '../components/ProductCard';
import useProductsStore from '../store/productsStore';
import './Home.css';

const Home = () => {
  const { featuredProducts, isLoading, fetchFeaturedProducts } = useProductsStore();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Create mailto link with form data
    const subject = `[IT World Inquiry] ${contactForm.service || 'General Inquiry'}`;
    const body = `Name: ${contactForm.name}\nEmail: ${contactForm.email}\nPhone: ${contactForm.phone || 'Not provided'}\nService Interest: ${contactForm.service || 'Not specified'}\n\nMessage:\n${contactForm.message}`;
    
    const mailtoLink = `mailto:itworldwani@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    
    // Show success message
    alert('Your email client will open with your message. If it doesn\'t, please email us directly at itworldwani@gmail.com');
    setContactForm({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const services = [
    {
      icon: <FaLaptopCode />,
      title: 'Web Development',
      description: 'Custom web applications using MERN stack (MongoDB, Express.js, React, Node.js)',
      color: '#0066ff'
    },
    {
      icon: <FaMobileAlt />,
      title: 'Android App Development',
      description: 'Cross-platform mobile applications using React Native',
      color: '#00d084'
    },
    {
      icon: <FaDesktop />,
      title: 'Desktop Applications',
      description: 'Professional desktop software using C# and .NET Framework',
      color: '#9333ea'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Antivirus Software',
      description: 'Quick Heal, Kaspersky, ESET and other premium antivirus solutions',
      color: '#ff6b35'
    },
    {
      icon: <FaWindows />,
      title: 'Microsoft Products',
      description: 'Genuine Windows OS and MS Office licenses for all your needs',
      color: '#0078d4'
    },
    {
      icon: <FaKey />,
      title: 'Software Licensing',
      description: 'Printer resetters, maintenance tools, and third-party software solutions',
      color: '#f59e0b'
    }
  ];

  const techStack = [
    { name: 'React', icon: <FaReact />, color: '#61dafb' },
    { name: 'Node.js', icon: <FaNodeJs />, color: '#339933' },
    { name: 'MongoDB', icon: <SiMongodb />, color: '#47a248' },
    { name: 'Express', icon: <SiExpress />, color: '#000000' },
    { name: 'React Native', icon: <SiReact />, color: '#61dafb' },
    { name: 'C#', icon: <SiCsharp />, color: '#239120' },
    { name: '.NET', icon: <SiDotnet />, color: '#512bd4' }
  ];

  const portfolioProjects = [
    {
      title: 'E-Commerce Platform',
      category: 'Web Development',
      description: 'Full-featured online store with payment integration',
      tech: 'MERN Stack'
    },
    {
      title: 'Inventory Management',
      category: 'Desktop App',
      description: 'Enterprise inventory tracking and management system',
      tech: 'C# & .NET'
    },
    {
      title: 'Food Delivery App',
      category: 'Mobile App',
      description: 'Cross-platform food ordering and delivery solution',
      tech: 'React Native'
    },
    {
      title: 'CRM Dashboard',
      category: 'Web Development',
      description: 'Customer relationship management with analytics',
      tech: 'MERN Stack'
    }
  ];

  const featuredSoftware = [
    { name: 'Windows 11 Pro', price: '₹3,999', icon: <FaWindows /> },
    { name: 'MS Office 2021', price: '₹2,499', icon: <FaKey /> },
    { name: 'Quick Heal Antivirus', price: '₹899', icon: <FaShieldAlt /> },
    { name: 'Kaspersky Total Security', price: '₹1,299', icon: <FaShieldAlt /> }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center min-vh-75">
            <Col lg={7} md={12}>
              <div className="hero-content animate-fade-in">
                <h1 className="hero-title">
                  Full-Stack Development & <span className="highlight">IT Software Solutions</span>
                </h1>
                <p className="hero-subtitle">
                  We build web, mobile & desktop apps — and provide trusted software solutions for your business.
                </p>
                <div className="hero-cta">
                  <Button variant="primary" size="lg" className="cta-button me-3 mb-3" href="#contact">
                    Get a Quote
                  </Button>
                  <Button as={Link} to="/products" variant="outline-light" size="lg" className="cta-button mb-3">
                    Browse Products
                  </Button>
                </div>
              </div>
            </Col>
            <Col lg={5} md={12} className="d-none d-lg-block">
              <div className="hero-illustration animate-float">
                <FaCode className="code-icon" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Section */}
      <section className="services-section" id="services">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">Comprehensive solutions for all your technology needs</p>
          </div>
          <Row>
            {services.map((service, index) => (
              <Col lg={4} md={6} className="mb-4" key={index}>
                <Card className="service-card h-100 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Card.Body className="text-center">
                    <div className="service-icon" style={{ color: service.color }}>
                      {service.icon}
                    </div>
                    <h4 className="service-title">{service.title}</h4>
                    <p className="service-description">{service.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Featured Software Products */}
      <section className="featured-software-section">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Featured Software Products</h2>
            <p className="section-subtitle">Genuine licenses at the best prices</p>
          </div>
          <Row className="justify-content-center">
            {featuredSoftware.map((software, index) => (
              <Col lg={3} md={6} sm={6} className="mb-4" key={index}>
                <Card className="software-card text-center animate-scale" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Card.Body>
                    <div className="software-icon">{software.icon}</div>
                    <h5 className="software-name">{software.name}</h5>
                    <p className="software-price">{software.price}</p>
                    <Button variant="primary" size="sm" as={Link} to="/products">
                      Get License
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-4">
            <Button as={Link} to="/products" variant="primary" size="lg">
              View All Products
            </Button>
          </div>
        </Container>
      </section>

      {/* Technology Stack Section */}
      <section className="tech-stack-section">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Technologies We Use</h2>
            <p className="section-subtitle">Modern tools for cutting-edge solutions</p>
          </div>
          <Row className="justify-content-center align-items-center">
            {techStack.map((tech, index) => (
              <Col lg={1} md={2} sm={3} xs={4} className="text-center mb-4" key={index}>
                <div className="tech-icon animate-bounce" style={{ animationDelay: `${index * 0.1}s`, color: tech.color }}>
                  {tech.icon}
                  <p className="tech-name">{tech.name}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Portfolio Section */}
      <section className="portfolio-section" id="portfolio">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Our Work</h2>
            <p className="section-subtitle">Recent projects we're proud of</p>
          </div>
          <Row>
            {portfolioProjects.map((project, index) => (
              <Col lg={6} md={6} className="mb-4" key={index}>
                <Card className="portfolio-card h-100 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Card.Body>
                    <div className="portfolio-category">{project.category}</div>
                    <h4 className="portfolio-title">{project.title}</h4>
                    <p className="portfolio-description">{project.description}</p>
                    <div className="portfolio-tech">
                      <span className="tech-badge">{project.tech}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Featured Products from Database */}
      {featuredProducts.length > 0 && (
        <section className="database-products-section">
          <Container>
            <h2 className="text-center mb-4 section-title">Popular Products</h2>
            {isLoading ? (
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <Row>
                {featuredProducts.map((product) => (
                  <Col key={product._id} lg={3} md={4} sm={6} className="mb-4">
                    <ProductCard product={product} />
                  </Col>
                ))}
              </Row>
            )}
          </Container>
        </section>
      )}

      {/* About Us Section */}
      <section className="about-section" id="about">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12} className="mb-4">
              <div className="about-image">
                <FaLaptopCode className="about-illustration" />
              </div>
            </Col>
            <Col lg={6} md={12}>
              <div className="about-content">
                <h2 className="section-title">About IT World</h2>
                <p className="about-text">
                  We are a full-service IT company specializing in custom software development and genuine software licensing. 
                  With years of experience in the industry, we deliver cutting-edge solutions tailored to your business needs.
                </p>
                <p className="about-text">
                  Our team of expert developers builds robust web applications, mobile apps, and desktop software using the 
                  latest technologies. We also provide authentic software licenses from leading brands at competitive prices.
                </p>
                <div className="about-stats mt-4">
                  <Row>
                    <Col xs={4} className="text-center mb-3">
                      <h3 className="stat-number">50+</h3>
                      <p className="stat-label">Projects Delivered</p>
                    </Col>
                    <Col xs={4} className="text-center mb-3">
                      <h3 className="stat-number">100+</h3>
                      <p className="stat-label">Happy Clients</p>
                    </Col>
                    <Col xs={4} className="text-center mb-3">
                      <h3 className="stat-number">5+</h3>
                      <p className="stat-label">Years Experience</p>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Get In Touch</h2>
            <p className="section-subtitle">Let's discuss your next project or software needs</p>
          </div>
          <Row>
            <Col lg={6} md={12} className="mb-4">
              <div className="contact-info">
                <h4 className="mb-4">Contact Information</h4>
                <div className="contact-item">
                  <FaPhone className="contact-icon" />
                  <div>
                    <h6>Phone</h6>
                    <p>9561306384</p>
                    <p className="text-muted">Mon-Sat, 11am-7pm</p>
                  </div>
                </div>
                <div className="contact-item">
                  <FaEnvelope className="contact-icon" />
                  <div>
                    <h6>Email</h6>
                    <p>itworldwani@gmail.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <FaWhatsapp className="contact-icon" />
                  <div>
                    <h6>WhatsApp</h6>
                    <p>9561306384</p>
                  </div>
                </div>
                <div className="contact-item">
                  <FaMapMarkerAlt className="contact-icon" />
                  <div>
                    <h6>Location</h6>
                    <p>Serving clients across India</p>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6} md={12}>
              <Card className="contact-form-card">
                <Card.Body>
                  <Form onSubmit={handleContactSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Your Name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="your.email@example.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="Your Phone Number"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Service/Product Interest</Form.Label>
                      <Form.Select
                        value={contactForm.service}
                        onChange={(e) => setContactForm({ ...contactForm, service: e.target.value })}
                      >
                        <option value="">Select an option</option>
                        <option value="web-development">Web Development</option>
                        <option value="mobile-development">Mobile App Development</option>
                        <option value="desktop-development">Desktop Application</option>
                        <option value="software-license">Software License</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Message *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Tell us about your requirements..."
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                      />
                    </Form.Group>
                    <Button variant="primary" type="submit" size="lg" className="w-100">
                      Send Message
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Home;
